import { ChevronRight, Home } from "lucide-react";
import { useEffect } from "react";
import { data, Link, NavLink, Outlet } from "react-router";
import { CompanyHeader } from "~/components/company/company-header";
import { CompanyJsonLd } from "~/components/company/company-jsonld";
import { CompanyDataFreshness } from "~/components/company/data-freshness";
import { CompanyDisclaimers } from "~/components/company/disclaimers";
import { BreadcrumbJsonLd } from "~/components/seo/breadcrumb-jsonld";
import { authHeadersFromRequest } from "~/lib/auth-headers.server";
import { companyCanonicalUrl, companyDisplayName, parseIcoSlug } from "~/lib/company-profile";
import { envConfig } from "~/lib/env-validation";
import { trackEvent } from "~/lib/gtag";
import { recordCompanyVisit } from "~/lib/hooks/use-recently-viewed";
import { cn } from "~/lib/utils";
import type { CompanyProfile } from "~/types/company";
import type { Route } from "./+types/company-layout";

// --- Loader ---

/** Upstream statuses worth retrying — transient throttling / gateway blips. */
const RETRYABLE_STATUS = new Set([429, 502, 503, 504]);
const RETRY_BACKOFF_MS = [150, 400];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Waits out the backoff for `attempt` and reports whether another try is
 * warranted. Returns false once attempts are exhausted or the client has
 * disconnected — so an aborted request neither sleeps nor re-fetches.
 */
async function shouldRetry(attempt: number, signal: AbortSignal): Promise<boolean> {
	if (signal.aborted || attempt >= RETRY_BACKOFF_MS.length) return false;
	await sleep(RETRY_BACKOFF_MS[attempt]);
	return !signal.aborted;
}

export async function loader({ params, request }: Route.LoaderArgs) {
	const ico = parseIcoSlug(params.icoSlug);
	const headers = await authHeadersFromRequest(request);
	const url = `${envConfig.apiBaseUrl}/companies/${ico}`;
	const { signal } = request;

	// The whole profile hinges on this fetch, so retry transient failures —
	// network errors, throttling, gateway blips, or a malformed success body —
	// before giving up. On final failure we surface 503 (not 500): it's an
	// upstream-unavailable condition, so crawlers should retry rather than treat
	// the page as permanently broken. Forwarding the request signal lets an
	// abandoned request abort instead of finishing its retries.
	for (let attempt = 0; ; attempt++) {
		let res: Response;
		try {
			res = await fetch(url, { headers, signal });
		} catch {
			if (await shouldRetry(attempt, signal)) continue;
			throw data(null, { status: 503 });
		}

		if (res.status === 404) {
			throw data(null, { status: 404 });
		}
		if (res.ok) {
			try {
				const profile: CompanyProfile = await res.json();
				const company = { ...profile, name: companyDisplayName(profile) };
				return { company };
			} catch {
				// Truncated / non-JSON 200 — treat like any other transient blip.
				if (await shouldRetry(attempt, signal)) continue;
				throw data(null, { status: 503 });
			}
		}
		if (RETRYABLE_STATUS.has(res.status) && (await shouldRetry(attempt, signal))) {
			continue;
		}
		throw data(null, { status: 503 });
	}
}

// --- Tab navigation ---

const TAB_BASE_CLASSES =
	"relative -mb-px inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors";

function tabClassName({ isActive }: { isActive: boolean }) {
	return cn(
		TAB_BASE_CLASSES,
		isActive
			? "border-primary font-semibold text-primary"
			: "border-transparent text-muted-foreground hover:text-foreground",
	);
}

function insolvencyTabClassName({ isActive }: { isActive: boolean }) {
	return cn(
		TAB_BASE_CLASSES,
		isActive
			? "border-red-600 font-semibold text-red-600"
			: "border-transparent text-red-600/80 hover:text-red-600",
	);
}

function CompanyTabNav({
	icoSlug,
	showInsolvency,
	hasActiveInsolvency,
}: {
	icoSlug: string;
	showInsolvency: boolean;
	hasActiveInsolvency: boolean;
}) {
	const base = `/firma/${icoSlug}`;
	const tabs = [
		{ to: base, label: "Přehled", end: true },
		{ to: `${base}/lide`, label: "Lidé" },
		{ to: `${base}/finance`, label: "Finance" },
		{ to: `${base}/historie`, label: "Historie" },
		{ to: `${base}/dokumenty`, label: "Dokumenty" },
		{ to: `${base}/smlouvy`, label: "Smlouvy" },
		{ to: `${base}/dph`, label: "DPH" },
		{ to: `${base}/zivnosti`, label: "Živnosti" },
	];

	return (
		<nav
			aria-label="Sekce profilu firmy"
			className="glass-panel-strong sticky top-20 z-10 mx-3 flex w-auto items-center gap-0 overflow-x-auto rounded-2xl px-2 scrollbar-none sm:mx-5 sm:px-4"
		>
			{tabs.map((tab) => (
				<NavLink key={tab.to} to={tab.to} end={tab.end} className={tabClassName} prefetch="intent">
					{tab.label}
				</NavLink>
			))}
			{showInsolvency && (
				<NavLink
					to={`${base}/insolvence`}
					className={hasActiveInsolvency ? insolvencyTabClassName : tabClassName}
					prefetch="intent"
				>
					Insolvence
				</NavLink>
			)}
			<NavLink to={`${base}/zakazky`} className={tabClassName} prefetch="intent">
				Zakázky
			</NavLink>
		</nav>
	);
}

// --- Component ---

export default function CompanyLayout({ loaderData, params }: Route.ComponentProps) {
	const { company } = loaderData;

	useEffect(() => {
		trackEvent("company_view", {
			company_ico: company.ico,
			company_name: company.name,
		});
		recordCompanyVisit({ ico: company.ico, name: company.name, city: company.city });
	}, [company.ico, company.name, company.city]);

	const breadcrumbItems = [
		{ name: "Domů", url: "/" },
		{ name: "Hledání", url: "/search" },
		{ name: company.name, url: companyCanonicalUrl(company) },
	];

	return (
		<div className="min-h-screen">
			<CompanyJsonLd company={company} />
			<BreadcrumbJsonLd items={breadcrumbItems} />

			<main className="mx-auto max-w-6xl px-4 py-6">
				<div className="flex flex-col gap-6">
					{/* Breadcrumbs */}
					<nav
						aria-label="Breadcrumb"
						className="glass-control flex w-fit max-w-full items-center gap-1.5 rounded-full px-3 py-2 text-sm"
					>
						<Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
							<Home className="h-4 w-4" />
						</Link>
						<ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
						<Link
							to="/search"
							className="text-muted-foreground hover:text-foreground transition-colors"
						>
							Hledání
						</Link>
						<ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
						<span className="font-medium truncate max-w-[200px] sm:max-w-none">{company.name}</span>
					</nav>

					{/* Elevated page sheet — header, tabs and content float on the lavender canvas */}
					<div className="sheet rounded-3xl">
						<div className="px-4 pt-6 pb-5 sm:px-6 lg:px-8 lg:pt-8">
							<CompanyHeader company={company} />
						</div>

						<CompanyTabNav
							icoSlug={params.icoSlug}
							showInsolvency={
								company.viewerTier !== "anon" &&
								(company.hasActiveInsolvency ||
									company.hasHistoricalInsolvency ||
									company.hasUnverifiedInsolvencyData ||
									company.adverseDataRestricted)
							}
							hasActiveInsolvency={company.hasActiveInsolvency}
						/>

						<div className="rounded-b-3xl bg-white/24 px-4 py-6 sm:px-6 lg:px-8">
							<Outlet />
						</div>
					</div>

					<CompanyDisclaimers disclaimers={company.disclaimers} />
					<CompanyDataFreshness freshness={company.dataFreshness} />
				</div>
			</main>
		</div>
	);
}
